<template>
  <BaseFormErrorSummary
    :errors="errors"
    :submit-count="submitCount"
  />

  <div class="flex gap-4 max-md:flex-col max-md:border-y max-md:border-gray-300 max-md:py-4 md:items-end ">

    <BaseStateSelect
      id="licenseState"
      v-model="values.licenseState"
      :country-id="countryId"
      label="License State or Province"
      name="licenseState"
      :required="hasStatesForLicense"
      :validation-rules="hasStatesForLicense ? 'required' : ''"
      @regions-change="onLicenseRegionsChange"
      @clear-state="setFieldValue('licenseState', '')"
    />

    <BaseSelectInput
      id="licenseType"
      v-model="values.licenseType"
      default-option-text="Select a license type"
      label="License Type"
      name="licenseType"
      required
      validation-rules="required"
      :disabled="licenseTypeDisabled"
    >
      <option
        v-for="profession in licenseTypes"
        :key="profession.id"
        :value="profession.id"
        v-text="profession.profession_name"
      />

    </BaseSelectInput>

    <BaseTextInput
      id="licenseNumber"
      v-model="values.licenseNumber"
      label="License Number"
      name="licenseNumber"
      required
      validation-rules="required"
      :disabled="licenseNumberDisabled"
    />
  </div>

    <div
      v-if="licenseStateSelected && !isLoadingLicenseTypes && licenseTypes.length === 0"
      class="body-sm mt-1 flex items-center gap-2 text-red"
    >
      <IconExclamationCircleFilled
        :size="20"
        aria-hidden="true"
        class="text-red"
      />
      <span v-text="noLicenseTypes"/>
    </div>

  <button
    :disabled="isSubmitting"
    class="btn btn--outline md:self-start"
    data-test-id="submit-license-button"
    type="button"
    @click="handleSubmit"
  >
    <span class="relative inline-flex items-center justify-center">
      <BaseLoadingIcon :visible="isLoading" />
      <span
        class="whitespace-nowrap"
        :class="{ 'invisible': isLoading }"
        v-text="license ? 'Update License' : 'Add License'"
      />
    </span>
  </button>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick, computed } from 'vue'
import { useForm } from 'vee-validate'
import { IconExclamationCircleFilled } from '@tabler/icons-vue'

import BaseStateSelect from '@js/components/global/forms/BaseStateSelect.vue'
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseSelectInput from '@js/components/global/forms/inputs/BaseSelectInput.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'

import { log } from '@js/utils/logger'
import { deleteCustomerLicense } from '@js/api/customer/deleteCustomerLicense'
import { upsertCustomerLicense } from '@js/api/customer/upsertCustomerLicense'
import { CustomerLicense } from '@js/api/types/customer/CustomerLicense'
import { getCustomerLicenseProfessions } from '@js/api/customer/getCustomerLicenseProfession'

import type { CustomerLicenseProfession } from '@js/api/types/customer/CustomerLicenseProfession'

defineOptions({
  name: 'LicenseForm',
})

const props = defineProps<{
  countryId: string
  customerId: number
  license: CustomerLicense | null
}>()

const emit = defineEmits<{
  clearState: [void]
  licenseDeleted: [licenseId: number]
  licenseAdded: [license: CustomerLicense]
}>()

const {
  values,
  errors,
  resetForm,
  setValues,
  submitCount,
  isSubmitting,
  setFieldValue,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    licenseType: props.license?.license_type || '',
    licenseState: props.license?.license_state || '',
    licenseNumber: props.license?.license_number || '',
  },
})

const licenseRegionCount = ref(0)
const hasStatesForLicense = computed(() => licenseRegionCount.value > 0)

const noLicenseTypes = 'There are no licenses for this state or province.'

const licenseTypes = ref<CustomerLicenseProfession[]>([])

const licenseTypeSelected = ref(false)
const licenseStateSelected = ref(false)

const licenseTypeDisabled = ref(true)
const licenseNumberDisabled = ref(true)

const isLoading = ref(false)
const isLoadingLicenseTypes = ref(false)
const isLoadingDelete = ref(false)

const hasManuallyChangedState = ref(false)
const updatingFromEdit = ref(false)

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  isLoading.value = true

  const selectedLicenseType = licenseTypes.value.find(
    (p) => String(p.id) === String(values.licenseType)
  )

  try {
    const customerLicense = await upsertCustomerLicense({
      licenseId: props.license ? props.license.license_id : null,
      customerId: props.customerId,
      licenseType: selectedLicenseType.profession_name,
      licenseState: values.licenseState,
      licenseNumber: values.licenseNumber,
      license_profession_id: parseInt(values.licenseType, 10),
    })

    emit('licenseAdded', customerLicense)
    emit('clearState')
  } catch (error) {
    log({
      message: 'Add License Error',
      file: 'src/wcei/js/components/account/profile/LicenseForm.vue',
    })
  } finally {
    resetForm()
    isLoading.value = false
  }
})

/**
 * Handle changes in the list of regions for the license state select.
 */
const onLicenseRegionsChange = (list: Array<{label: string; value: string}>) => {
  licenseRegionCount.value = Array.isArray(list) ? list.length : 0

  if (licenseRegionCount.value === 0) {
    setFieldValue('licenseState', '', false)
    licenseTypes.value = []
    setFieldValue('licenseType', '', false)
    licenseTypeDisabled.value = true
    licenseNumberDisabled.value = true
    licenseStateSelected.value = false
  }
}

/**
 * Watch for changes in licenseState and load the corresponding license types.
 * If the selected license type is no longer valid, reset it.
 */
watch(
  () => values.licenseState,
  async (newState, oldState) => {
    if (!newState) {
      setFieldValue('licenseType', '', false)
      return
    }

    // Snapshot BEFORE any mutation
    const prevLicenseType = values.licenseType

    // Only mark manual when not coming from an Edit-driven setValues()
    if (!updatingFromEdit.value && oldState && newState !== oldState) {
      hasManuallyChangedState.value = true
    }

    if (hasManuallyChangedState.value && !updatingFromEdit.value) {
      setFieldValue('licenseType', '', false)
      setFieldValue('licenseNumber', '', false)
    }

    licenseTypes.value = []
    isLoadingLicenseTypes.value = true

    try {
      const professions = await getCustomerLicenseProfessions(newState)
      licenseTypes.value = professions

      const norm = (s: unknown) => String(s ?? '').trim().toLowerCase()

      // Support both id-or-name in prev value
      const matched =
        professions.find((p) => String(p.id) === String(prevLicenseType)) ??
        professions.find((p) => norm(p.profession_name) === norm(prevLicenseType))

      if (matched) {
        setFieldValue('licenseType', String(matched.id), false)
        licenseNumberDisabled.value = false
      } else {
        setFieldValue('licenseType', '', false)
        licenseNumberDisabled.value = true
      }

      licenseTypeDisabled.value = professions.length === 0
    } catch (error) {
      console.error('Failed to load professions:', error)
    } finally {
      licenseStateSelected.value = true
      isLoadingLicenseTypes.value = false
    }
  }
)


/**
 * Watch for changes in licenseType and enable licenseNumber field when a type is selected.
 */
watch(
  () => values.licenseType,
  async (newState) => {
    licenseTypeSelected.value = true
    licenseNumberDisabled.value = false
  }
)

watch(
  () => props.license,
  async (newValue) => {
    if (newValue) {
      updatingFromEdit.value = true
      hasManuallyChangedState.value = false

      setValues({
        licenseType: newValue.license_type,
        licenseState: newValue.license_state,
        licenseNumber: newValue.license_number,
      })

      await nextTick()
      updatingFromEdit.value = false
    } else {
      resetForm()
    }
  },
)
</script>
